import React, { useState } from 'react';
import { faWarehouse, faPlus, faEdit, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearchParams } from 'react-router-dom';
import useGetRequest from '../../api/useGetRequest';
import EmptyList from '../../components/agv-empty-list';
import GenericPageContainer from '../../components/agv-generic-page-container';
import GenericTable from '../../components/agv-generic-table';
import GenericTableSkeleton from '../../components/agv-generic-table/genericTableSkeleton';
import HeaderButton from '../../components/agv-header-button';
import CreateWarehouseModal from './create-warehouse-modal';
import OnboardingBanner from '../../components/onboarding/OnboardingBanner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './warehouses.css';

// Fix for default markers not showing
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function Warehouses() {
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedFactory, setSelectedFactory] = useState(null);
  
  const { data, isLoading, mutate } = useGetRequest('/factories');

  const onOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateSuccess = () => {
    mutate();
  };

  const handleEdit = (factory) => {
    // TODO: Ouvrir le modal d'édition
    console.log('Éditer:', factory);
  };

  const handleDisable = (factory) => {
    // TODO: Désactiver l'entrepôt
    console.log('Désactiver:', factory);
  };

  const factories = data?.data || [];

  return (
    <>
      <GenericPageContainer
        title={'Entrepôts'}
        subtitle={'Gérez les entrepôts et leurs emplacements'}
        filters={
          <>
            <HeaderButton
              onClick={onOpenCreateModal}
              text="Créer un entrepôt"
              icon={faPlus}
            />
          </>
        }
      >
        {isOnboarding && (
          <OnboardingBanner
            step="1/2"
            title="Créez votre premier entrepôt"
            description="Les entrepôts sont les plateformes de départ de vos camions. Commencez par créer au moins un entrepôt pour continuer."
          />
        )}
        
        {isLoading ? (
          <GenericTableSkeleton numberOfRows={8} />
        ) : factories?.length > 0 ? (
          <div className="warehouse-layout">
            <div className="warehouse-list-container">
              <GenericTable
                isLoading={isLoading}
                rows={factories?.map((factory) => {
                  const vehicleKeys = factory.vehicle_keys ? factory.vehicle_keys.split(',') : [];
                  const vehicleLabels = factory.vehicle_labels ? factory.vehicle_labels.split(',') : [];
                  const vehiclesCount = parseInt(factory.vehicles_count) || 0;

                  return {
                    title: factory.name || 'Sans nom',
                    subtitle: factory.street
                      ? `${factory.street}${factory.zipcode || factory.city ? `, ${factory.zipcode || ''} ${factory.city || ''}` : ''}`
                      : 'Adresse non renseignée',
                    icon: faWarehouse,
                    statusText: factory.latitude && factory.longitude ? 'Géolocalisé' : 'Non géolocalisé',
                    fields: [
                      {
                        name: 'Véhicules rattachés',
                        value: (
                          <div className="warehouse-vehicles-list">
                            <span className="warehouse-vehicles-count">
                              {vehiclesCount} {vehiclesCount > 1 ? 'véhicules' : 'véhicule'}
                            </span>
                            {vehiclesCount > 0 && (
                              <div className="warehouse-vehicles-details">
                                {vehicleKeys.slice(0, 5).map((key, idx) => (
                                  <span key={key} className="warehouse-vehicle-tag" title={vehicleLabels[idx] || key}>
                                    {key}
                                  </span>
                                ))}
                                {vehiclesCount > 5 && (
                                  <span className="warehouse-vehicle-tag">+{vehiclesCount - 5}</span>
                                )}
                              </div>
                            )}
                          </div>
                        ),
                      },
                      {
                        name: 'Coordonnées GPS',
                        value: factory.latitude && factory.longitude
                          ? `${parseFloat(factory.latitude).toFixed(4)}, ${parseFloat(factory.longitude).toFixed(4)}`
                          : '-',
                      },
                      {
                        name: 'Actions',
                        value: (
                          <div className="warehouse-item-actions">
                            <button
                              className="warehouse-action-btn warehouse-action-edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(factory);
                              }}
                              title="Modifier"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="warehouse-action-btn warehouse-action-disable"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDisable(factory);
                              }}
                              title="Désactiver"
                            >
                              <FontAwesomeIcon icon={faBan} />
                            </button>
                          </div>
                        ),
                      },
                    ],
                  };
                })}
                iconColor={'tw-text-primary'}
              />
            </div>

            <div className="warehouse-map-container">
              <MapContainer
                center={[46.603354, 1.888334]}
                zoom={6}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%', borderRadius: '12px' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                />
                {factories
                  .filter((factory) => factory.latitude && factory.longitude)
                  .map((factory) => (
                    <Marker
                      key={factory.id}
                      position={[
                        parseFloat(factory.latitude),
                        parseFloat(factory.longitude),
                      ]}
                    >
                      <Popup>
                        <div className="warehouse-popup">
                          <strong>{factory.name}</strong>
                          <br />
                          {factory.street && (
                            <>
                              {factory.street}
                              <br />
                            </>
                          )}
                          {factory.city && (
                            <>
                              {factory.zipcode} {factory.city}
                            </>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
              </MapContainer>
            </div>
          </div>
        ) : (
          <EmptyList />
        )}
      </GenericPageContainer>
      <CreateWarehouseModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
}

